import React, { useEffect, useState } from "react";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import { Loader2, FileText, MessageSquare, FolderOpen, User as UserIcon } from "lucide-react";
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const t = {
  en: {
    profile: "Profile", deals: "Deals", documents: "Documents", messages: "Messages",
    email: "Email", ref: "Client Ref.", roles: "Roles", joined: "Joined",
    noDeal: "No deals found", noDoc: "No documents found", noMsg: "No messages found",
    stage: "Stage", price: "Price", type: "Type", status: "Status", date: "Date",
    direction: "Direction", body: "Body",
  },
  fr: {
    profile: "Profil", deals: "Dossiers", documents: "Documents", messages: "Messages",
    email: "Email", ref: "Réf. Client", roles: "Rôles", joined: "Inscription",
    noDeal: "Aucun dossier trouvé", noDoc: "Aucun document trouvé", noMsg: "Aucun message trouvé",
    stage: "Étape", price: "Prix", type: "Type", status: "Statut", date: "Date",
    direction: "Direction", body: "Contenu",
  },
};

interface UserDetailDrawerProps {
  open: boolean;
  onClose: () => void;
  user: any | null;
}

export function UserDetailDrawer({ open, onClose, user }: UserDetailDrawerProps) {
  const { language } = useLanguage();
  const labels = t[language as keyof typeof t] || t.en;
  const [deals, setDeals] = useState<any[]>([]);
  const [docs, setDocs] = useState<any[]>([]);
  const [msgs, setMsgs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !user) return;
    const uid = user.id;
    setLoading(true);

    Promise.all([
      // Deals où l'user est seller
      getDocs(query(collection(db, 'deals'), where('sellerId', '==', uid), limit(20))),
      getDocs(query(collection(db, 'dealDocuments'), where('seller_id', '==', uid), limit(20))),
      getDocs(query(collection(db, 'smsMessages'), where('assignedAgentId', '==', uid), orderBy('createdAt', 'desc'), limit(20))),
    ]).then(([dealsSnap, docsSnap, msgsSnap]) => {
      setDeals(dealsSnap.docs.map(d => ({
        id: d.id, ...d.data(),
        business_name: d.data().businessName || d.data().business_name || 'Unnamed',
        current_stage: d.data().currentStage || d.data().current_stage || 'submission',
        reference_number: d.data().referenceNumber || null,
        asking_price: d.data().askingPrice || d.data().asking_price || null,
        created_at: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })));
      setDocs(docsSnap.docs.map(d => ({
        id: d.id, ...d.data(),
        created_at: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })));
      setMsgs(msgsSnap.docs.map(d => ({
        id: d.id, ...d.data(),
        from_number: d.data().fromNumber || d.data().from_number || '',
        to_number: d.data().toNumber || d.data().to_number || '',
        created_at: d.data().createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
      })));
    }).finally(() => setLoading(false));
  }, [open, user]);

  if (!user) return null;

  const displayName = user.displayName || user.name || user.email || '—';
  const roles = user.roles?.length ? user.roles : user.role ? [user.role] : [];

  return (
    <Drawer open={open} onOpenChange={(o) => !o && onClose()}>
      <DrawerContent className="max-h-[85vh] bg-gray-900 border-white/10 text-white">
        <DrawerHeader className="border-b border-white/10 pb-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
              <UserIcon className="w-6 h-6 text-amber-400" />
            </div>
            <div>
              <DrawerTitle className="text-white text-lg">{displayName}</DrawerTitle>
              <DrawerDescription className="text-gray-400 text-sm">{user.email}</DrawerDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {user.clientReference || user.client_reference ? (
              <Badge variant="outline" className="text-amber-400 border-amber-500/30 font-mono text-xs">
                {user.clientReference || user.client_reference}
              </Badge>
            ) : null}
            {roles.map((r: string) => (
              <Badge key={r} variant="outline" className="text-xs border-white/20 text-gray-300">{r}</Badge>
            ))}
          </div>
        </DrawerHeader>

        <Tabs defaultValue="deals" className="flex-1">
          <TabsList className="bg-white/5 border-b border-white/10 rounded-none w-full justify-start px-4">
            <TabsTrigger value="deals" className="text-gray-400 data-[state=active]:text-amber-400 data-[state=active]:bg-white/10">
              <FolderOpen className="w-4 h-4 mr-1.5" /> {labels.deals} ({deals.length})
            </TabsTrigger>
            <TabsTrigger value="documents" className="text-gray-400 data-[state=active]:text-amber-400 data-[state=active]:bg-white/10">
              <FileText className="w-4 h-4 mr-1.5" /> {labels.documents} ({docs.length})
            </TabsTrigger>
            <TabsTrigger value="messages" className="text-gray-400 data-[state=active]:text-amber-400 data-[state=active]:bg-white/10">
              <MessageSquare className="w-4 h-4 mr-1.5" /> {labels.messages} ({msgs.length})
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-white/30" /></div>
          ) : (
            <ScrollArea className="max-h-[50vh] px-4 py-3">
              <TabsContent value="deals" className="mt-0 space-y-2">
                {deals.length === 0 && <p className="text-gray-500 text-sm py-6 text-center">{labels.noDeal}</p>}
                {deals.map((d) => (
                  <div key={d.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium text-white text-sm">{d.business_name}</p>
                        <p className="text-xs text-gray-400">{d.reference_number}</p>
                      </div>
                      <Badge variant="outline" className="text-xs border-amber-500/30 text-amber-300">{d.current_stage}</Badge>
                    </div>
                    {d.asking_price && <p className="text-xs text-gray-400 mt-1">{labels.price}: {Number(d.asking_price).toLocaleString()} {d.currency}</p>}
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="documents" className="mt-0 space-y-2">
                {docs.length === 0 && <p className="text-gray-500 text-sm py-6 text-center">{labels.noDoc}</p>}
                {docs.map((d) => (
                  <div key={d.id} className="bg-white/5 rounded-lg p-3 border border-white/10 flex justify-between">
                    <div>
                      <p className="font-medium text-white text-sm">{d.document_reference || d.document_type}</p>
                      <p className="text-xs text-gray-400">{new Date(d.created_at).toLocaleDateString()}</p>
                    </div>
                    <Badge variant="outline" className="text-xs border-white/20 text-gray-300">{d.status}</Badge>
                  </div>
                ))}
              </TabsContent>

              <TabsContent value="messages" className="mt-0 space-y-2">
                {msgs.length === 0 && <p className="text-gray-500 text-sm py-6 text-center">{labels.noMsg}</p>}
                {msgs.map((m) => (
                  <div key={m.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                    <div className="flex justify-between">
                      <p className="text-xs text-gray-400">{m.direction} — {m.from_number} → {m.to_number}</p>
                      <p className="text-xs text-gray-500">{new Date(m.created_at).toLocaleDateString()}</p>
                    </div>
                    <p className="text-sm text-gray-200 mt-1 line-clamp-2">{m.body}</p>
                  </div>
                ))}
              </TabsContent>
            </ScrollArea>
          )}
        </Tabs>
      </DrawerContent>
    </Drawer>
  );
}